import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Injectable } from '@angular/core';

import {
  TRANSCRIPT_UPDATE,
  TranscriptUpdatePayload,
  TranscriptWsEnvelope,
} from './transcript.types';
import { mapTranscriptPayloadToMessage } from './transcript-mapping';
import { Message } from '@app/shared/models/app.model';

/**
 * Shared ingestion layer for live transcripts delivered over WebSocket.
 *
 * Each WS connection registers its incoming-event stream against a device id.
 * This service filters `transcript.update` events, maps them to `Message`, and
 * accumulates them into a rolling per-device array snapshot that mirrors the
 * Firestore `orderBy('timestamp','desc').limit(25).valueChanges()` contract the
 * downstream pipeline (`SpeechService`) was built against.
 */
@Injectable({ providedIn: 'root' })
export class TranscriptStreamService {
  /** Mirrors the old Firestore `.limit(25)`. */
  private readonly MAX_MESSAGES = 25;

  private readonly streams = new Map<string, BehaviorSubject<Message[]>>();
  private readonly buffers = new Map<string, Map<string, Message>>();
  private readonly subscriptions = new Map<string, Subscription>();

  /**
   * Wire a WS connection's incoming-event stream to a device id. Re-registering
   * the same device replaces the previous subscription (reconnect-safe).
   */
  register(deviceId: string, incoming$: Observable<TranscriptWsEnvelope>): void {
    if (!deviceId) return;
    this.unregister(deviceId);
    const sub = incoming$
      .pipe(filter((m) => m?.event === TRANSCRIPT_UPDATE))
      .subscribe((m) => this.ingest(deviceId, m.payload as TranscriptUpdatePayload));
    this.subscriptions.set(deviceId, sub);
  }

  /** Stop ingesting for a device. The accumulated snapshot is retained. */
  unregister(deviceId: string): void {
    this.subscriptions.get(deviceId)?.unsubscribe();
    this.subscriptions.delete(deviceId);
  }

  /** Live array-snapshots for a device (emits [] until events arrive). */
  streamFor(deviceId: string | undefined): Observable<Message[]> {
    // A license with no resolvable device uuid (neither uuid nor uuids) yields a
    // permanently-empty stream rather than polluting the per-device map.
    if (!deviceId) return new BehaviorSubject<Message[]>([]).asObservable();
    return this.ensureSubject(deviceId).asObservable();
  }

  private ingest(deviceId: string, payload: TranscriptUpdatePayload): void {
    if (!payload?.id) return;
    const buffer = this.ensureBuffer(deviceId);
    buffer.set(payload.id, mapTranscriptPayloadToMessage(payload));

    // newest-first, capped — matches the old Firestore query exactly.
    const sorted = Array.from(buffer.values()).sort((a, b) => b.timestamp - a.timestamp);
    const capped = sorted.slice(0, this.MAX_MESSAGES);

    // prune the buffer so memory stays bounded.
    if (sorted.length > this.MAX_MESSAGES) {
      const keep = new Set(capped.map((m) => m.documentID));
      for (const id of Array.from(buffer.keys())) {
        if (!keep.has(id)) buffer.delete(id);
      }
    }

    // Emit defensive shallow copies: downstream consumers (autocaption styling and
    // translation/is_encrypted overwrites in SpeechService) mutate Message objects in
    // place, which would otherwise corrupt our retained buffer. This mirrors the
    // fresh-object-per-snapshot semantics the pipeline had under Firestore valueChanges().
    this.ensureSubject(deviceId).next(capped.map((m) => ({ ...m })));
  }

  private ensureSubject(deviceId: string): BehaviorSubject<Message[]> {
    let subject = this.streams.get(deviceId);
    if (!subject) {
      subject = new BehaviorSubject<Message[]>([]);
      this.streams.set(deviceId, subject);
    }
    return subject;
  }

  private ensureBuffer(deviceId: string): Map<string, Message> {
    let buffer = this.buffers.get(deviceId);
    if (!buffer) {
      buffer = new Map<string, Message>();
      this.buffers.set(deviceId, buffer);
    }
    return buffer;
  }
}
